import React from "react";
import Modal from "react-modal";
import {
  CancelBtnContainer,
  Container,
  ContentContainer,
  DomainListContainer,
  TabBarContainer,
  TabContentContainer,
  TabContentLabel,
  TabLabel,
  TitleContainer,
  TopSection,
} from "./GoogleWorkspaceMemberSyncPreviewModal.styles";
import UI from "@mile-meeting/mile-ui";
import Callout from "uis/Callout";
import { TIntegrationCategory } from "apis/member-integration";
import { LoadingModal } from "uis/modal";

export type GoogleWorkspaceMemberSyncPreviewModalProps = {
  modalIsOpen: boolean;
  closeModal: () => void;
  syncCategory?: TIntegrationCategory;
  onHandleSync?: () => void;
  addMemberList?: string[];
  deleteMemberList?: string[];
};

const modalStyle = {
  content: {
    top: "50%",
    left: "50%",
    right: "auto",
    bottom: "auto",
    marginRight: "-50%",
    width: "600px",
    borderRadius: "20px",
    transform: "translate(-50%, -50%)",
    padding: 0,
    margin: 0,
  },
  overlay: {
    backgroundColor: "var(--component-color-material-dimmer)",
    zIndex: 1000,
  },
};
const GoogleWorkspaceMemberSyncPreviewModal: React.FC<GoogleWorkspaceMemberSyncPreviewModalProps> = ({
  modalIsOpen,
  syncCategory = "DOMAIN",
  closeModal,
  addMemberList = [],
  deleteMemberList = [],
  onHandleSync,
}) => {
  const [tab, setTab] = React.useState<"new" | "delete">("new");
  const [loading, setLoading] = React.useState(false);

  return (
    <Modal isOpen={modalIsOpen} onRequestClose={closeModal} style={modalStyle}>
      <Container>
        <CancelBtnContainer>
          <UI.IconButton.Normal
            type="primary"
            icon="x"
            size={20}
            color="var(--alias-color-label-normal)"
            onClick={closeModal}
          />
        </CancelBtnContainer>
        <TitleContainer>
          <UI.Icon name="google" size={24} />
          <UI.Typography type="heading02" fontStyle="bold" color="var(--alias-color-label-neutral)">
            Google Workspace 멤버 동기화
          </UI.Typography>
        </TitleContainer>
        <ContentContainer>
          <TopSection>
            <Callout
              context={`연동된 ${syncCategory === "DOMAIN" ? "도메인" : "조직"}에 속한 계정만 동기화가 진행됩니다.\n동기화 시, 신규 멤버는 Mile 회원가입 및 워크스페이스 멤버(Staff 권한)로\n등록되며, 삭제 멤버는 워크스페이스의 멤버 목록에서도 삭제됩니다.`}
            />
            <TabBarContainer>
              <TabLabel selected={tab === "new"} onClick={() => setTab("new")}>
                <UI.Typography
                  type="body01 normal"
                  fontStyle="bold"
                  color={tab === "new" ? "var(--alias-color-primary-normal)" : "var(--alias-color-label-alternative)"}
                >
                  신규 멤버 ({addMemberList.length ?? "0"}인)
                </UI.Typography>
              </TabLabel>
              <TabLabel selected={tab === "delete"} onClick={() => setTab("delete")}>
                <UI.Typography
                  type="body01 normal"
                  fontStyle="bold"
                  color={
                    tab === "delete" ? "var(--alias-color-primary-normal)" : "var(--alias-color-label-alternative)"
                  }
                >
                  삭제 멤버 ({deleteMemberList.length ?? "0"}인)
                </UI.Typography>
              </TabLabel>
            </TabBarContainer>
            <TabContentContainer>
              <TabContentLabel>
                <UI.Icon
                  name={tab === "new" ? "addPerson" : "deletePerson"}
                  size={20}
                  color={tab === "new" ? "var(--alias-color-status-positive)" : "var(--alias-color-status-negative)"}
                />
                <UI.Typography type="body01 normal" fontStyle="bold">
                  총{" "}
                  <span
                    style={{
                      color:
                        tab === "new" ? "var(--alias-color-status-positive)" : "var(--alias-color-status-negative)",
                    }}
                  >
                    {tab === "new" ? addMemberList.length : deleteMemberList.length}
                  </span>
                  인
                </UI.Typography>
              </TabContentLabel>
              <DomainListContainer>
                <UI.Typography
                  type="label01"
                  fontStyle="regular"
                  color="var(--alias-color-label-normal)"
                  style={{
                    whiteSpace: "pre-line",
                  }}
                >
                  {tab === "new"
                    ? addMemberList.length === 0
                      ? "신규 멤버가 없습니다."
                      : addMemberList.map((member) => `· ${member}`).join("\n")
                    : deleteMemberList.length === 0
                      ? "삭제된 멤버가 없습니다."
                      : deleteMemberList.map((member) => `· ${member}`).join("\n")}
                </UI.Typography>
              </DomainListContainer>
            </TabContentContainer>
          </TopSection>
          <UI.Button
            onClick={async () => {
              setLoading(true);
              await onHandleSync?.();
              setLoading(false);
              closeModal();
            }}
            title="동기화하기"
            size="large"
            style={{
              width: "100%",
            }}
          />
        </ContentContainer>
      </Container>
      <LoadingModal modalIsOpen={loading} title="GWS 멤버 정보를 동기화 중입니다." />
    </Modal>
  );
};

export default React.memo(GoogleWorkspaceMemberSyncPreviewModal);
